/**
 * Route -> ApplicationObject slug.
 *
 * The registry is deny-by-default, so a route missing from this map is hidden
 * from everyone. It is therefore written out explicitly rather than derived
 * from a label or a path convention: a typo here silently removes a screen, and
 * a silent removal is far harder to notice than a crash.
 *
 * Keys are the ROUTES values from app/constants. Slugs are seeded by migrations
 * 20260805230000 and ...240000 — 95 objects in total.
 *
 * Routes deliberately absent are listed at the bottom with the reason.
 */
export const ROUTE_SLUGS: Record<string, string> = {
  // ── Admin ────────────────────────────────────────────────────────────────
  "/home":                 "home",
  "/dashboard":            "home",
  "/services":             "admin.services",
  "/items":                "admin.items",
  "/packages":             "admin.packages",
  "/pricing":              "admin.pricing",
  "/remedy-catalog":       "admin.remedy_catalog",
  "/composer":             "admin.composer",
  "/package-approval":     "admin.package_approvals",
  "/payments":             "admin.payments",
  "/admin/bizcards":       "admin.bizcards",
  "/bizcalendar-review":   "admin.bizcalendar_review",
  "/reports":              "admin.reports",
  "/lov":                  "admin.lov",
  // Settings absorbed Follow-up Settings on 2026-08-11 and reuses its
  // ApplicationObject: the grant already exists, the audience is the same, and
  // a new slug would have meant a migration plus a re-grant just to rename the
  // door. `/followup-settings` still maps so the redirect keeps its gate.
  "/settings":             "admin.followup_settings",
  "/followup-settings":    "admin.followup_settings",
  "/report-limits":        "admin.report_limits",
  // Team & Security is the admin area's landing page (its tabs are the three
  // rows above); Job health is platform plumbing. Both are the admin ROOT
  // object, which the admin roles hold and nobody else does — without a
  // mapping they failed open to every staff login (BP-ISS-20260917-069).
  "/team-security":        "admin",
  "/job-health":           "admin",
  "/audit-log":            "admin.audit_log",
  "/users":                "admin.users",
  // /access replaces /role-permissions per the owner's review. Both point at
  // the same object so the existing nav entry keeps working until the new
  // screen ships and the old route is retired.
  "/access":               "admin.access",
  "/role-permissions":     "admin.access",

  // ── Pre-Sales ────────────────────────────────────────────────────────────
  "/pre-sales":              "home",
  "/pre-sales/leads":        "presales.leads",
  "/pre-sales/pipeline":     "presales.pipeline",
  "/pre-sales/followups":    "presales.followups",
  "/pre-sales/interactions": "presales.interactions",
  "/pre-sales/proposals":    "presales.proposals",
  "/pre-sales/whatsapp":     "presales.whatsapp",
  // Seeded by 20260925110000 and granted to exactly the holders of
  // contact:review — mapped so the rail never shows it to anyone else.
  "/pre-sales/intake-review": "presales.intake_review",
  // Seeded by 20260926140000, granted to exactly the holders of referral:read.
  "/pre-sales/referrals":     "presales.referrals",

  // ── Post-Sales ───────────────────────────────────────────────────────────
  "/post-sales":            "home",
  "/post-sales/companies":  "postsales.companies",
  "/post-sales/clients":    "postsales.clients",
  "/post-sales/delivery":   "postsales.delivery",
  "/post-sales/renewals":   "postsales.renewals",
  "/post-sales/new-interests": "postsales.new_interests",
  "/post-sales/community":  "postsales.community",
  "/post-sales/followups":  "postsales.followups",

  // ── Astrology workspace (spec §7.2; objects seeded by 20260807160000) ────
  "/astrology/kundali":          "astrology.kundali",
  // The Workbench is the Kundali screen's sibling and was unmapped — an
  // unmapped route fails OPEN and would have kept the Astrology tab alive for
  // a tenant with no astrology grant at all.
  "/astrology/workbench":        "astrology.kundali",
  "/astrology/bizcalendars":     "astrology.bizcalendars",
  "/astrology/review":           "astrology.review",
  "/astrology/reports":          "astrology.reports",
  "/astrology/astro-updates":    "astrology.astro_updates",
  "/astrology/remedy-catalogue": "astrology.remedy_catalogue",
  "/astrology/numerology":       "astrology.numerology",
  "/astrology/rules":            "astrology.rules",

  // ── HR workspace (objects seeded by 20260904120000) ──────────────────────
  // Mapped, unlike the Kundali Workbench was: an unmapped route fails OPEN in
  // the sidebar and advertises a screen most staff cannot open.
  "/hr":           "hr.directory",
  "/hr/directory": "hr.directory",
  "/hr/settings":  "hr.settings",
  "/hr/hiring":     "hr.hiring",
  "/hr/onboarding": "hr.onboarding",
  "/hr/attendance": "hr.attendance",
  "/hr/shifts":     "hr.shifts",
  "/hr/approvals":  "hr.approvals",
  "/hr/leave":      "hr.leave",
  "/hr/reconciliation": "hr.reconciliation",
  "/hr/payroll":    "hr.payroll",
  "/hr/separation": "hr.separation",
  // Mapped 2026-09-22, which CLOSES the fail-open these two relied on. They
  // were unmapped from 2026-09-17 because their grants sat with the two admin
  // roles alone, and hiding them would have taken away the two HR screens a
  // rep actually uses. 20260922160000 grants all four self-scoped HR screens
  // to every role but CLIENT and AUDITOR, so the rail can now tell the truth.
  "/hr/calendar":   "hr.calendar",
  "/hr/pulse":      "hr.pulse",
  "/tasks":         "admin.tasks",
  "/tasks/types":   "admin.task_lists",
  "/tasks/performance": "admin.performance",

  // ── Client Portal ────────────────────────────────────────────────────────
  "/portal":               "portal.home",
  "/portal/reports":       "portal.reports",
  "/portal/bizcalendar":   "portal.bizcalendar",
  "/portal/remedies":      "portal.remedies",
  "/portal/dasha":         "portal.dasha",
  "/portal/numerology":    "portal.numerology",
  "/portal/astro-updates": "portal.astro_updates",
  "/portal/payments":      "portal.payments",
  "/portal/packages":      "portal.packages",
  "/portal/community":     "portal.community",
  "/portal/refer":         "portal.refer",
  "/portal/profile":       "portal.profile",
};

/**
 * Routes intentionally NOT gated, and why. Listed rather than merely omitted so
 * the absence reads as a decision instead of an oversight.
 *
 *   /login, /register, /forgot-password,
 *   /reset-password, /confirm-email-change   unauthenticated by design
 *   /pending-approval                        shown precisely when a user has
 *                                            no access yet
 *   /event-mode, /ld/, /videoAssesment       public marketing surfaces
 *   /intake/employee/:token                  the new hire's own intake form (no login yet)
 *   detail routes (/…/:id, /…/new)           inherit the parent screen's grant;
 *                                            gating them separately would mean
 *                                            a registry entry per record
 */
export const UNGATED_ROUTES: readonly string[] = [
  "/login", "/register", "/forgot-password", "/reset-password",
  "/confirm-email-change", "/pending-approval",
  "/event-mode", "/ld/", "/videoAssesment",
  "/consent/walk-in/", "/intake/employee/",
];

/** Slug for a route, or undefined when the route is ungated or unknown. */
export function slugForRoute(path: string): string | undefined {
  return ROUTE_SLUGS[path];
}

/**
 * Slug for the longest mapped route that is a segment-aligned prefix of
 * `pathname` — `/lov/abc/edit` → `/lov`, `/settings/tags` → `/settings`,
 * `/post-sales/clients/<id>` → `/post-sales/clients`. Detail routes inherit
 * the parent screen's grant (see UNGATED_ROUTES). Undefined when nothing
 * mapped is a prefix.
 */
export function slugForRoutePrefix(pathname: string): string | undefined {
  let best: string | undefined;
  for (const route of Object.keys(ROUTE_SLUGS)) {
    if (pathname === route || pathname.startsWith(route + "/")) {
      if (!best || route.length > best.length) best = route;
    }
  }
  return best ? ROUTE_SLUGS[best] : undefined;
}
